<template>
  <div v-if="permissions?.hasRead || $userInfo.isSystemAdministrator">
    <app-detail-header :show-toggle="true"
      :show-delete="false"
      :show-save="false"
      :show-cancel="false"
      :show-print="false"
      :show-new="true"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <user-list-side-menu v-if="$userInfo.isSystemAdministrator && !$userInfo.isSupportUser && !isCompanyUser"
          v-model="filter"
          @filter="filterList"
          @reset="resetFilter" />
      </div>
      <div class="column">
        <div class="m-3">
          <article class="box detail-page-tile">
            <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
              :columns="columns"
              :is-loading="isTableLoading"
              :page-index="filter.pageIndex"
              :page-size="filter.pageSize"
              :sort-column="filter.sortColumn"
              :sort-order="filter.sortOrder"
              :total-rows="totalRows"
              @pageChanged="onPageChange"
              @sort="sort">

              <tr v-for="(user, index) in users"
                :key="index">
                <td>
                  {{ user.username }}
                </td>
                <td>{{ user.fullName }}</td>
                <td class="has-text-centered">{{ user.active ? 'Yes' : 'No' }}</td>
                <td class="action-column"
                  :class="{ 'has-text-centered': !isCompanyUser }">
                  <router-link v-if="!isCompanyUser"
                    :to="{ path: `${user.userId}` }"
                    append>
                    <a class="button is-primary is-small is-inverted tooltip"
                      data-tooltip="Click to edit">
                      <span class="icon is-medium">
                        <span class="mso mso-24px">
                          edit
                        </span>
                      </span>
                    </a>
                  </router-link>

                  <div v-else>
                    <a class="is-primary is-small is-inverted tooltip"
                      data-tooltip="Reset Password"
                      @click="toggleResetPasswordModal(user)">
                      Reset Password
                    </a>
                  </div>
                </td>
              </tr>
              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <span icon="icon is-large">
                      <span class="mso mso-50px mso-fill">
                        sentiment_dissatisfied
                      </span>
                    </span>
                    <p>Nothing</p>
                  </div>
                </section>
              </template>
            </bulma-table>
          </article>
          <reset-password-modal v-if="showResetPasswordModal && isCompanyUser"
            :value="selectedUser"
            :active="showResetPasswordModal"
            @close="showResetPasswordModal = false" />
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="is-flex is-justify-content-center pt-5">
      <div class="notification is-danger is-flex is-size-4">
        You do not have permissions on this page
      </div>
    </div>
  </div>
</template>

<script>
import AppDetailHeader from '@/components/AppDetailHeader'
import { AppHeaderButtonTypes } from '@/enums'
import BulmaTable from '@/components/BulmaTable'
import { UserListColumns } from './columns'
import UserListSideMenu from './UserListSideMenu.vue'
import UserService from '@/services/UserService'
import StoreMixin from './storeMixin'
import ResetPasswordModal from './ResetPasswordModal.vue'

export default {
  name: 'UserListView',
  components: {
    AppDetailHeader,
    BulmaTable,
    UserListSideMenu,
    ResetPasswordModal
  },
  mixins: [StoreMixin],
  data() {
    return {
      filter: {
        name: '',
        userType: '',
        active: 1,
        sortColumn: this.defaultSortColumn,
        sortOrder: 'DESC',
        pageIndex: 1,
        pageSize: 30
      },
      isTableLoading: false,
      users: [],
      totalRows: 0,
      showResetPasswordModal: false,
      selectedUser: {}
    }
  },
  computed: {
    columns() {
      return UserListColumns
    },
    filterKey() {
      return this.$userInfo ? `${this.$userInfo.sessionId}|${this.$route.meta.fkey}` : ''
    },
    permissions() {
      var permissions = this.$user.permissions?.find((p) => p.codeName == this.$route.meta.id) || null
      return permissions
    },
    defaultSortColumn() {
      if (this.$userInfo.isSystemAccount && !this.isCompanyUser) {
        return 'CreatedDate'
      } else {
        return 'CreatedOnUtc'
      }
    },
    isCompanyUser() {
      const { companyId } = this.$route.params
      return !!companyId
    }
  },
  async created() {
    await this.getUsers()
  },
  methods: {
    filterList() {
      this.persistFilter()
      this.getUsers()
    },

    async getUsers() {
      this.isTableLoading = true
      this.retrieveFilter()
      if (this.isCompanyUser) {
        const { companyId } = this.$route.params
        this.users = await UserService.getUsersByCompanyId(companyId, this.filter)
      } else {
        this.users = await UserService.getUsers(this.filter)
      }
      this.totalRows = this.users.length > 0 ? this.users[0].totalRows : 0
      this.isTableLoading = false
    },

    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.AddNew:
          this.addNew()
          break
      }
    },

    onPageChange(pageIndex) {
      this.filter.pageIndex = pageIndex
      this.persistFilter()
      this.getUsers()
    },

    persistFilter() {
      sessionStorage.setItem(this.filterKey, JSON.stringify(this.filter))
    },

    resetFilter() {
      this.filter.name = ''
      this.filter.userType = ''
      this.filter.active = 1
      this.filter.sortColumn = ''
      this.filter.sortOrder = UserListColumns[0].defaultOrder
      this.filter.pageIndex = 1
      this.filter.pageSize = 30
      this.persistFilter()
      this.getUsers()
    },

    retrieveFilter() {
      const filter = JSON.parse(sessionStorage.getItem(this.filterKey))
      if (filter) {
        this.filter.name = filter.name
        this.filter.userType = filter.userType
        this.filter.active = filter.active
        this.filter.sortColumn = filter.sortColumn
        this.filter.sortOrder = filter.sortOrder
        this.filter.pageIndex = filter.pageIndex
        this.filter.pageSize = filter.pageSize
      }
      if (!this.filter.sortColumn) {
        this.filter.sortColumn = this.defaultSortColumn
      }
    },

    sort(name, order) {
      this.filter.sortColumn = name
      this.filter.sortOrder = order
      this.persistFilter()
      this.getUsers()
    },
    async addNew() {
      this.addStoreItem()
    },
    toggleResetPasswordModal(user) {
      this.selectedUser = user
      this.showResetPasswordModal = !this.showResetPasswordModal
    }
  }
}
</script>

<style lang="scss" scoped>
.action-column {
  width: 10%;
}
</style>